import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import { useSaveRegistration } from "../../../api/hooks";
import type { RegistrationProfile } from "../../../types";
import { SelectionField } from "./controls";

type Ref = { id: string; name: string };
type SectorRef = { id: string; name: string; category?: { name: string } | null };
type OrgType = { value: string; label: string };

/**
 * Step 2 — service range: the regions, sectors and organization types the
 * expert serves (company sizes are no longer asked). This is the pool the
 * per-enjeu scope in the expertise step is drawn from. Saved via PATCH,
 * advancing registration_step to 4.
 */
export function ServiceRangeStep({
  profile,
  regions,
  sectors,
  orgTypes,
  onSaved,
  onBack,
}: {
  profile: RegistrationProfile;
  regions: Ref[];
  sectors: SectorRef[];
  orgTypes: OrgType[];
  onSaved: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const save = useSaveRegistration();
  const [form, setForm] = useState<RegistrationProfile>(profile);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function set<K extends keyof RegistrationProfile>(
    key: K,
    value: RegistrationProfile[K],
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }
  const str = (k: keyof RegistrationProfile) => (form[k] as string | null) ?? "";
  const arr = (k: keyof RegistrationProfile) =>
    (form[k] as string[] | undefined) ?? [];
  const onScope = (k: keyof RegistrationProfile) => (next: string[]) =>
    set(k, next as RegistrationProfile[typeof k]);
  const onOther = (k: keyof RegistrationProfile) => (v: string) =>
    set(k, v as RegistrationProfile[typeof k]);

  // Sectors grouped by category (old app groups the switch list by category,
  // uncategorized sectors under "Autres"; categories sorted, "Autres" last).
  const sectorGroups = (() => {
    const uncategorized = t("expert.register.uncategorized");
    const map = new Map<string, { value: string; label: string }[]>();
    for (const s of sectors) {
      const heading = s.category?.name ?? uncategorized;
      const list = map.get(heading) ?? [];
      list.push({ value: s.id, label: s.name });
      map.set(heading, list);
    }
    return [...map.entries()]
      .sort(([a], [b]) =>
        a === uncategorized ? 1 : b === uncategorized ? -1 : a.localeCompare(b),
      )
      .map(([heading, options]) => ({ heading, options }));
  })();

  const REQUIRED_SCOPE: (keyof RegistrationProfile)[] = [
    "service_regions",
    "sectors_served",
    "organization_types_served",
  ];

  const handleContinue = () => {
    setError(null);
    const req = t("expert.register.validation.required");
    const found: Record<string, string> = {};
    for (const k of REQUIRED_SCOPE) if (arr(k).length === 0) found[k] = req;
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setError(t("expert.register.validation.fix_required"));
      return;
    }
    // Only this step's fields: re-sending the whole profile also re-sent the
    // organization step's (the location among them), which re-ran their rules
    // and re-derived the directory regions from a step that does not ask them.
    save.mutate(
      {
        service_regions: form.service_regions,
        sectors_served: form.sectors_served,
        sectors_served_other: form.sectors_served_other,
        organization_types_served: form.organization_types_served,
        organization_types_served_other: form.organization_types_served_other,
        registration_step: 4,
      },
      {
        onSuccess: () => onSaved(),
        onError: () => setError(t("auth.genericError")),
      },
    );
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">{t("expert.register.mandatoryNote")}</p>
      <section className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("expert.register.scopeIntro")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("expert.register.scopeHelp")}
        </p>
        <SelectionField
          label={t("expert.register.fields.service_regions")}
          options={regions.map((r) => ({ value: r.id, label: r.name }))}
          selected={arr("service_regions")}
          onChange={onScope("service_regions")}
          selectAllLabel={t("expert.register.selectAllRegions")}
          required
          error={errors.service_regions}
        />
        <SelectionField
          label={t("expert.register.fields.sectors_served")}
          options={sectors.map((s) => ({ value: s.id, label: s.name }))}
          groups={sectorGroups}
          selected={arr("sectors_served")}
          onChange={onScope("sectors_served")}
          selectAllLabel={t("expert.register.selectAllSectors")}
          otherLabel={t("expert.register.otherSectors")}
          otherValue={str("sectors_served_other")}
          onOtherChange={onOther("sectors_served_other")}
          required
          error={errors.sectors_served}
        />
        <SelectionField
          label={t("expert.register.fields.organization_types_served")}
          options={orgTypes.map((o) => ({ value: o.value, label: o.label }))}
          selected={arr("organization_types_served")}
          onChange={onScope("organization_types_served")}
          selectAllLabel={t("expert.register.selectAllTypes")}
          otherLabel={t("expert.register.otherTypes")}
          otherValue={str("organization_types_served_other")}
          onOtherChange={onOther("organization_types_served_other")}
          required
          error={errors.organization_types_served}
        />
      </section>

      {error && <p className="text-sm font-medium text-destructive">{error}</p>}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          {t("expert.register.back")}
        </Button>
        <Button onClick={handleContinue} disabled={save.isPending}>
          {t("expert.register.next")}
        </Button>
      </div>
    </div>
  );
}
